import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiArrowLeft, FiDownload, FiFileText, FiCalendar, FiHardDrive, FiEye, FiZap, FiSearch } from 'react-icons/fi';
import { authenticatedFetch } from '../utils/api';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
`;

const HeaderBar = styled.div`
  background: transparent;
  border-bottom: none;
  padding: 16px 32px;
  box-shadow: none;
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  min-height: 60px;
  gap: 16px;
  z-index: 1;
`;

const PortalTitle = styled.h1`
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  white-space: nowrap;
  grid-column: 2;
  justify-self: center;
  text-align: center;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  color: white;
  padding: 6px 16px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  white-space: nowrap;
  grid-column: 1;
  min-width: fit-content;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 24px;
  position: relative;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 6px;
`;

const PageTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const PageSubtitle = styled.p`
  display: none;
`;

const FilesTableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  gap: 0;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(208, 215, 222, 0.8);
  margin-bottom: 40px;
`;

const HeaderRowWrapper = styled.div`
  width: 100%;
  padding-right: ${props => props.$hasScrollbar ? '8px' : '0px'};
  box-sizing: border-box;
  transition: padding-right 0.1s ease;
  margin-bottom: 0;
`;

const FileRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 180px 200px;
  align-items: center;
  gap: 16px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: none;
  border-top: 1px solid rgba(208, 215, 222, 0.8);
  padding: 8px 20px;
  border-radius: 0;
  box-shadow: none;
  transition: all 0.2s ease;

  &:first-of-type {
    border-top: none;
  }

  &:hover {
    box-shadow: none;
    background: rgba(248, 250, 252, 0.95);
  }
`;

const FilesScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: calc(100vh - 350px);
  overflow-y: auto;
  padding-right: 0;
  padding-bottom: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const FileIcon = styled.div`
  font-size: 1.5rem;
  color: #0077B6;
  flex-shrink: 0;
`;

const FileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
  justify-content: center;
`;

const FileName = styled.h3`
  color: #333333;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileMeta = styled.div`
  display: flex;
  gap: 16px;
  font-size: 0.8rem;
  color: #666666;
`;

const FileSize = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FileDate = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  flex-wrap: nowrap;
`;

const ViewButton = styled.button`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  color: #333333;
  padding: 6px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  justify-content: center;

  &:hover {
    background: #f5f5f5;
  }

  &:active {
    background: #e8e8e8;
  }
`;

const DownloadButton = styled.button`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  color: #333333;
  padding: 6px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  justify-content: center;

  &:hover {
    background: #f5f5f5;
  }

  &:active {
    background: #e8e8e8;
  }
`;

const InteractiveLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  background: transparent;
  backdrop-filter: none;
  border-radius: 0;
  box-shadow: none;
  min-height: 400px;
  border: none;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 24px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
  text-align: center;
  letter-spacing: 0.3px;
`;

const LoadingProgress = () => {
  return (
    <InteractiveLoadingContainer>
      <LoadingSpinner />
      <LoadingText>Loading PDF Files</LoadingText>
    </InteractiveLoadingContainer>
  );
};

const ErrorMessage = styled.div`
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 20px;
  color: #92400e;
  text-align: center;
  margin: 20px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const EmptyText = styled.h3`
  font-size: 1.5rem;
  margin: 0 0 10px 0;
`;

const EmptySubtext = styled.p`
  font-size: 1rem;
  opacity: 0.7;
  margin: 0;
`;

const SearchBarContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 2px solid #d0d7de;
  padding: 14px 18px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #0077B6;
    box-shadow: 0 0 0 4px rgba(0, 119, 182, 0.1);
  }
`;

const SearchIcon = styled(FiSearch)`
  color: #666666;
  margin-right: 12px;
  font-size: 1.2rem;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 1rem;
  color: #333333;
  background: transparent;

  &::placeholder {
    color: #999999;
  }
`;

const PdfListPage = ({ plant, onBack }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    fetchPdfFiles();
  }, [plant.id]);

  const fetchPdfFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`/api/plants/${plant.id}/files/pdf`);
      const data = await response.json();
      
      if (data.success) {
        setFiles(data.files || []);
      } else {
        setError(data.error || 'Failed to fetch files');
      }
    } catch (err) {
      setError('Failed to fetch PDF files');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileName = (filePath) => {
    return filePath.split('/').pop();
  };

  // Filter files based on search query
  const filteredFiles = files.filter(file => {
    if (!searchQuery.trim()) return true;
    const fileName = getFileName(file.name).toLowerCase();
    const query = searchQuery.toLowerCase().trim();
    return fileName.includes(query);
  });


  const handleView = async (file) => {
    try {
      // Fetch PDF as blob using authenticated fetch
      const response = await authenticatedFetch(`/api/plants/${plant.id}/files/view/${encodeURIComponent(file.name)}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unauthorized' }));
        throw new Error(errorData.error || 'Failed to load PDF');
      }
      
      // Check if response is actually a PDF
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/pdf')) {
        // If not PDF, try to get error message
        const text = await response.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || 'Response is not a PDF file');
        } catch {
          throw new Error('Response is not a PDF file');
        }
      }
      
      // Read response as array buffer first, then create blob
      const arrayBuffer = await response.arrayBuffer();
      
      if (arrayBuffer.byteLength === 0) {
        throw new Error('PDF file is empty');
      }
      
      // Create blob from array buffer with explicit MIME type
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      
      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);
      
      // Open in new window
      const newWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      
      if (!newWindow || newWindow.closed) {
        // Fallback: create download link
        const link = document.createElement('a');
        link.href = blobUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Clean up blob URL after a delay (browser will keep it in memory while viewing)
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
      
    } catch (error) {
      alert(`Failed to open PDF: ${error.message || 'Unauthorized'}. Please try downloading instead.`);
    }
  };

  const handleDownload = async (file) => {
    try {
      // Fetch PDF as blob using authenticated fetch
      const response = await authenticatedFetch(`/api/plants/${plant.id}/files/view/${encodeURIComponent(file.name)}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unauthorized' }));
        throw new Error(errorData.error || 'Failed to download PDF');
      }
      
      // Check if response is actually a PDF
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/pdf')) {
        throw new Error('Response is not a PDF file');
      }
      
      // Read response as array buffer
      const arrayBuffer = await response.arrayBuffer();
      
      if (arrayBuffer.byteLength === 0) {
        throw new Error('PDF file is empty');
      }
      
      // Create blob from array buffer
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      
      // Create blob URL and trigger download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = getFileName(file.name);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
      
    } catch (error) {
      alert(`Failed to download file: ${error.message || 'Unauthorized'}. Please try again.`);
    }
  };

  return (
    <PageContainer>
      <MainContent>
        <ContentWrapper>
          <PageHeader>
            <PageTitle>
              {plant.id} {plant.name} PDF
            </PageTitle>
          </PageHeader>

          {loading && (
            <LoadingProgress />
          )}

          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}

          {!loading && !error && files.length === 0 && (
            <EmptyState>
              <EmptyIcon>
                <FiFileText />
              </EmptyIcon>
              <EmptyText>No PDF files found</EmptyText>
              <EmptySubtext>
                There are no PDF files available for {plant.id} at this time.
              </EmptySubtext>
            </EmptyState>
          )}

          {!loading && !error && files.length > 0 && (
            <>
              <SearchBarContainer>
                <SearchBar>
                  <SearchIcon />
                  <SearchInput
                    type="text"
                    placeholder="Search files by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </SearchBar>
              </SearchBarContainer>
              {filteredFiles.length === 0 ? (
                <EmptyState>
                  <EmptyIcon>
                    <FiFileText />
                  </EmptyIcon>
                  <EmptyText>No files match your search</EmptyText>
                  <EmptySubtext>
                    No PDF files found matching "{searchQuery}"
                  </EmptySubtext>
                </EmptyState>
              ) : (
                <FilesTableWrapper>
                  <HeaderRowWrapper>
                    <FileRow style={{ background: '#e8edf1', fontWeight: 700, padding: '8px 20px', border: 'none', borderBottom: '1px solid #d0d7de', boxShadow: 'none' }}>
                      <div style={{ color: '#333333', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>File Name</div>
                      <div style={{ color: '#333333', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Date</div>
                      <div style={{ color: '#333333', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Actions</div>
                    </FileRow>
                  </HeaderRowWrapper>
                  <FilesScrollContainer>
                    {filteredFiles.map((file, index) => (
                      <FileRow key={index}>
                        <FileInfo>
                          <FileIcon>
                            <FiFileText />
                          </FileIcon>
                          <FileDetails>
                            <FileName>{getFileName(file.name)}</FileName>
                          </FileDetails>
                        </FileInfo>
                        <FileMeta>
                          <FileDate>
                            <FiCalendar />
                            {formatDate(file.lastModified)}
                          </FileDate>
                        </FileMeta>
                        <ButtonGroup>
                          <ViewButton onClick={() => handleView(file)}>
                            <FiEye />
                            View
                          </ViewButton>
                          <DownloadButton onClick={() => handleDownload(file)}>
                            <FiDownload />
                            Download
                          </DownloadButton>
                        </ButtonGroup>
                      </FileRow>
                    ))}
                  </FilesScrollContainer>
                </FilesTableWrapper>
              )}
            </>
          )}
        </ContentWrapper>
      </MainContent>
    </PageContainer>
  );
};

export default PdfListPage;
